import {
  CAMERA,
  NETWORK,
  PLAYER_MOVEMENT,
  ZONE,
  clamp,
  clampToBounds,
  lerpAngle,
} from "@mucki/shared";
import type { AnimationState } from "@mucki/shared";

export interface MovementInput {
  /** 1 forward, -1 backward. */
  forward: number;
  /** 1 right, -1 left. */
  strafe: number;
  jump: boolean;
}

export interface MovementState {
  x: number;
  y: number;
  z: number;
  rotationY: number;
  verticalVelocity: number;
  grounded: boolean;
  moving: boolean;
}

export const NO_INPUT: MovementInput = { forward: 0, strafe: 0, jump: false };

export function createMovementState(x = 0, z = 0): MovementState {
  return {
    x,
    y: ZONE.groundHeight,
    z,
    rotationY: 0,
    verticalVelocity: 0,
    grounded: true,
    moving: false,
  };
}

/**
 * Kinematic, camera-relative movement (KTD3): no physics engine, a flat ground
 * plane, and zone bounds as the only collision. Pure so the client controller
 * and its tests share one implementation.
 */
export function stepMovement(
  state: MovementState,
  input: MovementInput,
  cameraYaw: number,
  delta: number,
): MovementState {
  const next: MovementState = { ...state, moving: false };

  const magnitude = Math.hypot(input.forward, input.strafe);
  if (magnitude > 0) {
    // Normalised so diagonal input is not faster than a single axis.
    const forward = input.forward / magnitude;
    const strafe = input.strafe / magnitude;
    // At yaw 0 the camera looks down -Z, so forward is -Z and right is +X.
    const forwardX = -Math.sin(cameraYaw);
    const forwardZ = -Math.cos(cameraYaw);
    const rightX = Math.cos(cameraYaw);
    const rightZ = -Math.sin(cameraYaw);

    const stepX = (forwardX * forward + rightX * strafe) * PLAYER_MOVEMENT.speed * delta;
    const stepZ = (forwardZ * forward + rightZ * strafe) * PLAYER_MOVEMENT.speed * delta;

    next.x += stepX;
    next.z += stepZ;
    next.moving = true;
    next.rotationY = lerpAngle(
      state.rotationY,
      Math.atan2(stepX, stepZ),
      clamp(delta * CAMERA.turnRate, 0, 1),
    );
  }

  if (input.jump && next.grounded) {
    next.verticalVelocity = PLAYER_MOVEMENT.jumpVelocity;
    next.grounded = false;
  }

  if (!next.grounded) {
    next.y += next.verticalVelocity * delta;
    next.verticalVelocity += PLAYER_MOVEMENT.gravity * delta;
    if (next.y <= ZONE.groundHeight) {
      next.y = ZONE.groundHeight;
      next.verticalVelocity = 0;
      next.grounded = true;
    }
  }

  const clamped = clampToBounds(next.x, next.z);
  next.x = clamped.x;
  next.z = clamped.z;

  return next;
}

/** The movement-driven part of R9; combat states are owned by the server. */
export function animationStateFor(state: MovementState): AnimationState {
  if (!state.grounded) return "jumping";
  return state.moving ? "moving" : "idle";
}

/**
 * Eases the local cat toward the server's accepted position once the two
 * diverge past the threshold (KTD6) — no input replay, just a soft pull.
 */
export function applyServerCorrection(
  state: MovementState,
  accepted: { x: number; z: number },
  threshold: number,
  rate: number,
  delta: number,
): MovementState {
  const divergence = Math.hypot(accepted.x - state.x, accepted.z - state.z);
  if (divergence <= threshold) return state;

  // A respawn or a teleport is not a prediction error to ease away.
  if (divergence >= NETWORK.correctionSnapDistance) {
    return { ...state, x: accepted.x, z: accepted.z };
  }

  const t = clamp(delta * rate, 0, 1);
  return {
    ...state,
    x: state.x + (accepted.x - state.x) * t,
    z: state.z + (accepted.z - state.z) * t,
  };
}
